import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, CheckCircle2, XCircle,
  Clock, Loader2, Zap, BarChart3, ArrowLeft,
  ExternalLink, Globe, Images,
  FolderTree, ChevronLeft, ChevronRight,
} from "lucide-react";

const STATUS_CONFIG = {
  done:       { label: "Tamamlandı", color: "bg-green-100 text-green-700",  icon: CheckCircle2 },
  processing: { label: "İşleniyor",  color: "bg-blue-100 text-blue-700",    icon: Loader2 },
  queued:     { label: "Kuyrukta",   color: "bg-yellow-100 text-yellow-700", icon: Clock },
  failed:     { label: "Hata",       color: "bg-red-100 text-red-700",       icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-gray-100 text-gray-600", icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.color}`}>
      <Icon className={`h-3 w-3 ${status === "processing" ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
}

function ConfidenceBar({ score }) {
  if (score == null) return <span className="text-xs text-muted-foreground">—</span>;
  const pct = Math.round(score * 100);
  const color = pct >= 75 ? "bg-green-500" : pct >= 40 ? "bg-yellow-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full bg-gray-200">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">%{pct}</span>
    </div>
  );
}

export default function AIIntelligencePage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(1);
  const [productMeta, setProductMeta] = useState({ total: 0, pages: 1 });
  const [productsLoading, setProductsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tab, setTab] = useState("jobs"); // "jobs" | "products"
  const [stats, setStats] = useState({ total: 0, done: 0, pending: 0 });

  const loadJobs = useCallback(async () => {
    try {
      const data = await api.get("/ai/products/jobs?limit=50");
      setJobs(data.jobs || []);
      const done = (data.jobs || []).filter(j => j.status === "done").length;
      setStats(s => ({ ...s, total: data.count, done, pending: data.count - done }));
    } catch {
      toast.error("Joblar yüklenemedi");
    }
  }, []);

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const categoryParam = categoryId ? `&category_id=${encodeURIComponent(categoryId)}` : "";
      const [data, categoryData] = await Promise.all([
        api.get(`/ai/products/catalog?page=${page}&limit=50${categoryParam}`),
        api.get("/categories"),
      ]);
      setProducts(data.products || []);
      setProductMeta({ total: data.total || 0, pages: data.pages || 1 });
      setCategoryCounts(data.category_counts || {});
      setCategories(categoryData || []);
    } catch {
      toast.error("Ürünler yüklenemedi");
    } finally {
      setProductsLoading(false);
    }
  }, [page, categoryId]);

  useEffect(() => {
    Promise.all([loadJobs(), loadProducts()]).finally(() => setLoading(false));
  }, [loadJobs, loadProducts]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, categoryId]);

  const categoryMap = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);
  const groupedProducts = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    products.forEach(product => {
      const parent = categoryMap.get(product.category_id);
      const child = categoryMap.get(product.subcategory_id);
      const key = product.category_id || "uncategorized";
      if (!byKey.has(key)) {
        const group = {
          key,
          name: parent?.name || product.category || "Kategorisiz Ürünler",
          color: parent?.color || "#94a3b8",
          products: [],
        };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).products.push({ ...product, _subcategoryName: child?.name || "" });
    });
    return groups;
  }, [products, categoryMap]);

  // Seçili ürünleri toplu zenginleştir
  const handleBulkEnrich = async (apply = false) => {
    if (selectedIds.size === 0) { toast.error("Ürün seçin"); return; }
    setBulkLoading(true);
    try {
      const result = await api.post("/ai/products/enrich/bulk", {
        product_ids: [...selectedIds],
        apply,
      });
      toast.success(`${result.queued} ürün kuyruğa eklendi`);
      setSelectedIds(new Set());
      setTab("jobs");
      setTimeout(loadJobs, 2000);
    } catch (err) {
      toast.error(err.message || "Toplu işlem başarısız");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkImages = async () => {
    if (selectedIds.size === 0) { toast.error("Ürün seçin"); return; }
    setBulkLoading(true);
    try {
      const result = await api.post("/ai/products/images/bulk", {
        product_ids: [...selectedIds],
        apply: true,
      });
      toast.success(`${result.queued} ürün için fotoğraf araması kuyruğa eklendi`);
      setSelectedIds(new Set());
      setTab("jobs");
      setTimeout(loadJobs, 2000);
    } catch (err) {
      toast.error(err.message || "Toplu fotoğraf araması başarısız");
    } finally {
      setBulkLoading(false);
    }
  };

  const pushToWoo = async (productId) => {
    try {
      const r = await api.post(`/ai/products/woocommerce/push/${productId}`);
      toast.success(`WooCommerce'e gönderildi: ${r.pushed_fields?.join(", ")}`);
    } catch (err) {
      toast.error(err.message || "WooCommerce push başarısız");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  const selectCategory = (nextCategoryId) => {
    setCategoryId(nextCategoryId);
    setPage(1);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-lg border p-2 hover:bg-muted">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            AI Ürün Zekası
          </h1>
          <p className="text-sm text-muted-foreground">Ürün içeriklerini ve fotoğraflarını bağımsız ya da toplu zenginleştirin</p>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Toplam İşlem", value: stats.total, icon: BarChart3, color: "text-violet-600" },
          { label: "Tamamlanan", value: stats.done, icon: CheckCircle2, color: "text-green-600" },
          { label: "Bekleyen", value: stats.pending, icon: Clock, color: "text-yellow-600" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Icon className={`h-4 w-4 ${color}`} />
              <span className="text-xs font-medium">{label}</span>
            </div>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      {/* Sekmeler */}
      <div className="flex gap-1 rounded-lg border bg-muted/30 p-1 w-fit">
        {[["jobs", "İşlem Geçmişi"], ["products", "Ürünler"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === key ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Jobs sekmesi */}
      {tab === "jobs" && (
        <div className="rounded-xl border bg-card">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="text-sm font-medium">İşlem Geçmişi</span>
            <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={loadJobs}>
              <RefreshCw className="h-3 w-3" /> Yenile
            </Button>
          </div>
          {jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Sparkles className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">Henüz işlem yok</p>
              <p className="text-xs mt-1">Ürünler sekmesinden zenginleştirme başlatın</p>
            </div>
          ) : (
            <div className="divide-y">
              {jobs.map(job => (
                <div key={job.id} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{job.product_name || job.product_id}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {job.created_at ? new Date(job.created_at).toLocaleString("tr-TR") : ""}
                    </p>
                  </div>
                  <ConfidenceBar score={job.confidence_score} />
                  <StatusBadge status={job.status} />
                  {job.status === "done" && job.result?.detected_category && (
                    <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {job.result.detected_category}
                    </span>
                  )}
                  {job.job_type === "images" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">
                      <Images className="h-3 w-3" /> Fotoğraf
                    </span>
                  )}
                  {job.status === "done" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => navigate(`/panel/urunler/${job.product_id}`)}
                    >
                      <ExternalLink className="h-3 w-3" /> Ürüne Git
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products sekmesi */}
      {tab === "products" && (
        <div className="overflow-hidden rounded-xl border bg-card">
          <div className="border-b bg-slate-50/70 px-4 py-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FolderTree className="h-4 w-4 text-violet-600" /> Kategori çalışma alanı
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">Her sayfa en fazla 50 ürün içerir ve tek toplu işleme hazırdır.</p>
              </div>
              <span className="rounded-full border bg-white px-3 py-1 text-xs text-slate-600">{productMeta.total} ürün · Sayfa {page}/{productMeta.pages}</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => selectCategory("")}
                className={`shrink-0 rounded-lg border px-3 py-2 text-left transition ${!categoryId ? "border-violet-300 bg-violet-50 text-violet-800 shadow-sm" : "bg-white hover:border-slate-300"}`}
              >
                <span className="block text-xs font-semibold">Tüm kategoriler</span>
                <span className="text-[10px] opacity-70">{Object.values(categoryCounts).reduce((sum, count) => sum + count, 0)} ürün</span>
              </button>
              {categories.filter(c => !c.parent_id && categoryCounts[c.id]).map(category => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => selectCategory(category.id)}
                  className={`shrink-0 rounded-lg border px-3 py-2 text-left transition ${categoryId === category.id ? "border-violet-300 bg-violet-50 text-violet-800 shadow-sm" : "bg-white hover:border-slate-300"}`}
                >
                  <span className="flex items-center gap-1.5 text-xs font-semibold">
                    <i className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color || "#8b5cf6" }} />
                    {category.name}
                  </span>
                  <span className="text-[10px] opacity-70">{categoryCounts[category.id]} ürün</span>
                </button>
              ))}
              {categoryCounts.uncategorized > 0 && (
                <button
                  type="button"
                  onClick={() => selectCategory("uncategorized")}
                  className={`shrink-0 rounded-lg border px-3 py-2 text-left transition ${categoryId === "uncategorized" ? "border-violet-300 bg-violet-50 text-violet-800 shadow-sm" : "bg-white hover:border-slate-300"}`}
                >
                  <span className="block text-xs font-semibold">Kategorisiz</span>
                  <span className="text-[10px] opacity-70">{categoryCounts.uncategorized} ürün</span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedIds.size === products.length && products.length > 0}
                onChange={toggleAll}
                className="rounded"
              />
              <span className="text-sm font-medium">
                {selectedIds.size > 0 ? `${selectedIds.size} / ${products.length} ürün seçildi` : `Bu sayfadaki ${products.length} ürün`}
              </span>
            </div>
            {selectedIds.size > 0 && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => handleBulkEnrich(false)}
                  disabled={bulkLoading}
                >
                  {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  İçerik Önizle
                </Button>
                <Button
                  size="sm"
                  className="h-7 gap-1.5 text-xs bg-violet-600 hover:bg-violet-700"
                  onClick={() => handleBulkEnrich(true)}
                  disabled={bulkLoading}
                >
                  {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                  İçeriği Uygula
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 border-sky-200 text-xs text-sky-700 hover:bg-sky-50"
                  onClick={handleBulkImages}
                  disabled={bulkLoading}
                >
                  {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Images className="h-3 w-3" />}
                  Fotoğraf Bul
                </Button>
              </div>
            )}
          </div>
          {productsLoading ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-500" /></div>
          ) : groupedProducts.length === 0 ? (
            <div className="py-14 text-center text-sm text-muted-foreground">Bu kategoride ürün bulunmuyor.</div>
          ) : (
            <div className="bg-slate-50/40 p-3 space-y-3">
              {groupedProducts.map(group => (
                <section key={group.key} className="overflow-hidden rounded-lg border bg-white">
                  <div className="flex items-center justify-between border-b bg-slate-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <i className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: group.color }} />
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-700">{group.name}</span>
                      <Badge variant="secondary" className="h-5 rounded-md text-[10px]">{group.products.length}</Badge>
                    </div>
                    <Button
                      type="button" size="sm" variant="ghost" className="h-6 text-[11px]"
                      onClick={() => setSelectedIds(prev => {
                        const next = new Set(prev);
                        const allSelected = group.products.every(p => next.has(p.id));
                        group.products.forEach(p => allSelected ? next.delete(p.id) : next.add(p.id));
                        return next;
                      })}
                    >
                      {group.products.every(p => selectedIds.has(p.id)) ? "Seçimi kaldır" : "Kategoriyi seç"}
                    </Button>
                  </div>
                  <div className="divide-y">
                    {group.products.map(p => (
                      <div key={p.id} className="flex items-center gap-3 px-3 py-3 hover:bg-slate-50/80">
                        <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} className="rounded" />
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-medium">{p.name}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {p.sku}{p.brand ? ` · ${p.brand}` : ""}{p._subcategoryName ? ` · ${p._subcategoryName}` : ""}
                          </p>
                        </div>
                        {p.ai_enrichment_status && <StatusBadge status={p.ai_enrichment_status} />}
                        {p.ai_confidence_score != null && <ConfidenceBar score={p.ai_confidence_score} />}
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => navigate(`/panel/urunler/${p.id}`)}>
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                          {p.wc_id && p.ai_enrichment_status === "done" && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-blue-600" onClick={() => pushToWoo(p.id)} title="WooCommerce'e push et">
                              <Globe className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between border-t px-4 py-3">
            <span className="text-xs text-muted-foreground">Sayfa başına 50 ürün</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={page <= 1 || productsLoading} onClick={() => setPage(p => Math.max(1, p - 1))}>
                <ChevronLeft className="h-3.5 w-3.5" /> Önceki
              </Button>
              <span className="min-w-16 text-center text-xs font-medium">{page} / {productMeta.pages}</span>
              <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={page >= productMeta.pages || productsLoading} onClick={() => setPage(p => Math.min(productMeta.pages, p + 1))}>
                Sonraki <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
